// packages/scoring-types/src/novel-dimensions.ts
// FRAME Scoring Engine v3 — Proprietary dimensions beyond Apple/Google/NIMA
//
// PRIOR ART ANALYSIS (March 2026):
//   - NIMA (Google 2017): Single aesthetic score 1-10. No transparency. No dimensions.
//   - BRISQUE/NIQE: Technical distortion only. No aesthetics.
//   - Diffusion Aesthetics (Stable Diffusion): Single scalar, training-data-dependent.
//   - PhotoILike: Real estate focus, single scalar.
//   - NTIRE 2024 Portrait Quality: Two-branch (face + background), still single output.
//   - KonIQ-10k, SPAQ, LIVE-ITW: Benchmark datasets, not scoring systems.
//   - Apple CalculateImageAestheticsScoresRequest: Single score, no breakdown, iOS 18+ only.
//   - Google ML Kit: Labels only, no quality scoring.
//
// WHAT NO ONE DOES:
//   Multi-dimensional transparent scoring with mode-specific weighting AND
//   these novel proprietary dimensions layered on top of base Vision/ML Kit analysis.
//   Each dimension below uses standard CV primitives but applies them in combinations
//   that don't exist in any published system or commercial product.
//
// PATENT RELEVANCE:
//   These dimensions strengthen FRAME's combination claim. The base 4 dimensions
//   (portrait, lighting, composition, scene) use Apple/Google APIs. These 7 novel
//   dimensions are FRAME's own analysis that no competitor can replicate without
//   licensing, because the specific application to photo curation scoring is novel.

// ─── Novel Dimension Definitions ────────────────────────────

export interface NovelDimensionScores {
  colorHarmony: number | null;        // 0-10: Itten color theory compliance
  depthPerception: number | null;     // 0-10: Apparent 3D depth quality
  authenticityIndex: number | null;   // 0-10: Natural vs. over-processed
  subjectEnvironment: number | null;  // 0-10: Subject-background harmony
  gazeDynamics: number | null;        // 0-10: Eye contact, group engagement (null if no faces)
  emotionalResonance: number | null;  // 0-10: Predicted emotional impact
  temporalPeak: number | null;        // 0-10: Best-moment detection in burst/series (null if solo)
}

export type NovelDimensionKey =
  | 'colorHarmony'
  | 'depthPerception'
  | 'authenticityIndex'
  | 'subjectEnvironment'
  | 'gazeDynamics'
  | 'emotionalResonance'
  | 'temporalPeak';

export interface NovelDimensionMeta {
  key: NovelDimensionKey;
  label: string;
  icon: string;
  shortLabel: string;           // for compact bar display
  description: string;          // tooltip/info text
  naturalLanguageLabel: string; // for attunement sliders
  noteForScore: (score: number) => string;
  requiresFace: boolean;
  requiresBurst: boolean;
}

export const NOVEL_DIMENSIONS: NovelDimensionMeta[] = [
  {
    key: 'colorHarmony',
    label: 'Color Harmony',
    icon: 'palette',
    shortLabel: 'Harmony',
    description: 'How well the colors work together based on color theory — complementary, analogous, triadic, or split-complementary relationships.',
    naturalLanguageLabel: 'How important is color harmony?',
    noteForScore: (s) =>
      s >= 8 ? 'Beautiful complementary palette' :
      s >= 6 ? 'Pleasant color relationships' :
      s >= 4 ? 'Colors are acceptable' :
      'Clashing or muddy palette',
    requiresFace: false,
    requiresBurst: false,
  },
  {
    key: 'depthPerception',
    label: 'Depth & Dimension',
    icon: 'layers',
    shortLabel: 'Depth',
    description: 'The sense of three-dimensional space — lens blur gradients, atmospheric perspective, overlapping forms, and foreground-background separation.',
    naturalLanguageLabel: 'How much does depth matter?',
    noteForScore: (s) =>
      s >= 8 ? 'Rich sense of depth and dimension' :
      s >= 6 ? 'Good spatial layering' :
      s >= 4 ? 'Somewhat flat composition' :
      'Very flat, no depth cues',
    requiresFace: false,
    requiresBurst: false,
  },
  {
    key: 'authenticityIndex',
    label: 'Authenticity',
    icon: 'shield-check',
    shortLabel: 'Authentic',
    description: 'How natural and unprocessed the photo looks — penalizes heavy HDR, oversaturation, aggressive noise reduction, and filter artifacts.',
    naturalLanguageLabel: 'Prefer natural or processed look?',
    noteForScore: (s) =>
      s >= 8 ? 'Beautifully natural, minimal processing' :
      s >= 6 ? 'Tasteful, light editing' :
      s >= 4 ? 'Noticeably processed' :
      'Heavily filtered or artificial',
    requiresFace: false,
    requiresBurst: false,
  },
  {
    key: 'subjectEnvironment',
    label: 'Subject-Scene Fit',
    icon: 'puzzle-piece',
    shortLabel: 'Fit',
    description: 'How well the subject belongs in the environment — color temperature match, complementary tones, natural positioning.',
    naturalLanguageLabel: 'How much does subject-background fit matter?',
    noteForScore: (s) =>
      s >= 8 ? 'Subject and scene perfectly matched' :
      s >= 6 ? 'Good visual harmony' :
      s >= 4 ? 'Subject somewhat disconnected from scene' :
      'Subject looks out of place',
    requiresFace: true,
    requiresBurst: false,
  },
  {
    key: 'gazeDynamics',
    label: 'Gaze & Engagement',
    icon: 'eye',
    shortLabel: 'Gaze',
    description: 'Eye contact quality, gaze direction, and interpersonal engagement in group photos — are people connected or distracted?',
    naturalLanguageLabel: 'How important are eye contact and engagement?',
    noteForScore: (s) =>
      s >= 8 ? 'Direct, engaging eye contact' :
      s >= 6 ? 'Good natural gaze' :
      s >= 4 ? 'Distracted or averted gaze' :
      'Eyes closed, looking away, disengaged',
    requiresFace: true,
    requiresBurst: false,
  },
  {
    key: 'emotionalResonance',
    label: 'Emotional Impact',
    icon: 'heart-pulse',
    shortLabel: 'Impact',
    description: 'Predicted emotional response — combines color psychology, facial expression analysis, compositional tension, and visual storytelling cues.',
    naturalLanguageLabel: 'How much should emotional impact matter?',
    noteForScore: (s) =>
      s >= 8 ? 'Powerful, emotionally compelling' :
      s >= 6 ? 'Evokes a clear feeling' :
      s >= 4 ? 'Neutral, not particularly moving' :
      'Flat emotional register',
    requiresFace: false,
    requiresBurst: false,
  },
  {
    key: 'temporalPeak',
    label: 'Peak Moment',
    icon: 'clock-arrow-up',
    shortLabel: 'Peak',
    description: 'In a burst or series, how well this frame captures the peak expression or moment — smile apex, action peak, no blinks.',
    naturalLanguageLabel: 'Optimize for peak moments in bursts?',
    noteForScore: (s) =>
      s >= 8 ? 'Perfect peak moment captured' :
      s >= 6 ? 'Good timing, near peak' :
      s >= 4 ? 'Slightly off-peak' :
      'Caught mid-blink or awkward transition',
    requiresFace: true,
    requiresBurst: true,
  },
];

// ─── Color Harmony Theory (Itten) ───────────────────────────

export type ColorRelationship =
  | 'complementary'    // opposite on wheel (highest contrast)
  | 'analogous'        // adjacent on wheel (most harmonious)
  | 'triadic'          // evenly spaced 3
  | 'split-complementary' // complement + adjacent
  | 'tetradic'         // rectangle on wheel
  | 'monochromatic'    // single hue, varied value/saturation
  | 'discordant';      // no identifiable relationship

export interface ColorHarmonyResult {
  relationship: ColorRelationship;
  confidence: number;      // 0-1
  dominantHues: number[];  // degrees on color wheel (0-360)
  saturationVariance: number;
  valueVariance: number;
  score: number;           // 0-10
}

// Scoring multipliers per relationship type per mode
export const HARMONY_MODE_WEIGHTS: Record<string, Record<ColorRelationship, number>> = {
  balanced:     { complementary: 1.2, analogous: 1.3, triadic: 1.1, 'split-complementary': 1.15, tetradic: 1.0, monochromatic: 1.1, discordant: 0.6 },
  social:       { complementary: 1.1, analogous: 1.4, triadic: 1.0, 'split-complementary': 1.1, tetradic: 0.9, monochromatic: 1.2, discordant: 0.5 },
  instagram:    { complementary: 1.3, analogous: 1.2, triadic: 1.3, 'split-complementary': 1.2, tetradic: 1.1, monochromatic: 1.0, discordant: 0.7 },
  travel:       { complementary: 1.2, analogous: 1.1, triadic: 1.2, 'split-complementary': 1.1, tetradic: 1.0, monochromatic: 0.9, discordant: 0.7 },
  realEstate:   { complementary: 1.0, analogous: 1.4, triadic: 0.9, 'split-complementary': 1.0, tetradic: 0.8, monochromatic: 1.3, discordant: 0.4 },
  professional: { complementary: 1.1, analogous: 1.3, triadic: 1.0, 'split-complementary': 1.1, tetradic: 0.9, monochromatic: 1.4, discordant: 0.3 },
  custom:       { complementary: 1.0, analogous: 1.0, triadic: 1.0, 'split-complementary': 1.0, tetradic: 1.0, monochromatic: 1.0, discordant: 1.0 },
};

// ─── Depth Perception Cues ──────────────────────────────────

export interface DepthCues {
  blurGradient: number;        // 0-1: strength of focus falloff (bokeh)
  atmosphericPerspective: number; // 0-1: haze/lightening in distance
  sizeConstancy: number;       // 0-1: near-far size variation
  overlappingForms: number;    // 0-1: objects overlapping (depth layering)
  foregroundPresence: number;  // 0-1: strong foreground element
  textureGradient: number;     // 0-1: detail reduction with distance
}

// ─── Authenticity Detection ─────────────────────────────────

export interface AuthenticitySignals {
  hdrArtifacts: number;        // 0-1: 0 = natural, 1 = extreme HDR
  oversaturation: number;      // 0-1: 0 = natural, 1 = blown out
  noisePatterns: number;       // 0-1: 0 = clean, 1 = heavy NR artifacts
  filterDetected: boolean;     // Instagram-style filter detected
  compressionArtifacts: number; // 0-1: JPEG banding/blocking
  unnaturalSharpening: number; // 0-1: halo artifacts from oversharpening
  score: number;               // 0-10: higher = more authentic
}

// ─── Gaze & Engagement Analysis ─────────────────────────────

export interface GazeResult {
  primaryGazeAngle: number;       // degrees from camera (0 = direct)
  eyeContactScore: number;       // 0-10: quality of camera engagement
  groupEngagement: number;       // 0-10: are people looking at each other?
  blinkDetected: boolean;
  squintLevel: number;           // 0-1
  gazeDirections: Array<{        // per-face
    faceIndex: number;
    yaw: number;                 // horizontal gaze direction
    pitch: number;               // vertical gaze direction
    isEngaged: boolean;          // looking at camera or group members
  }>;
}

// ─── Emotional Resonance ────────────────────────────────────

export interface EmotionalResonanceResult {
  // Color psychology signals
  warmthIndex: number;           // 0-1: warm colors evoke different emotions
  contrastDrama: number;         // 0-1: high contrast = tension/power
  colorTemperatureEmotion: string; // 'warm-intimate', 'cool-calm', 'neutral', 'dramatic'

  // Facial expression signals (if faces present)
  expressionIntensity: number;   // 0-1: strength of detected expression
  expressionValence: number;     // -1 to 1: negative to positive
  microExpressionDetected: boolean;

  // Compositional tension
  visualTension: number;         // 0-1: dynamic angles, leading lines, implied motion
  negativeSpace: number;         // 0-1: breathing room (can be powerful or lonely)

  score: number;                 // 0-10
}

// ─── Temporal Peak Detection ────────────────────────────────

export interface TemporalPeakResult {
  burstSize: number;             // total frames in burst
  peakFrameIndex: number;        // which frame is best
  isPeakFrame: boolean;          // is THIS the peak frame?
  expressionPeakScore: number;   // 0-1: smile/expression at max
  blinkFreeScore: number;        // 0-1: no blinks detected
  motionBlurScore: number;       // 0-1: sharpest frame
  overallPeakScore: number;      // 0-10
}

// ─── Extended PhotoScore (v3) ───────────────────────────────

export interface PhotoScoreV3 {
  // Base dimensions (v2 — Apple/Google APIs)
  base: {
    portraitComposition: number | null;
    lighting: number;
    composition: number;
    sceneInterest: number;
    sceneInterestSpiked: number;
  };

  // Novel dimensions (v3 — FRAME proprietary)
  novel: NovelDimensionScores;

  // Combined
  baseComposite: number;         // weighted from base dims only
  novelComposite: number;        // weighted from novel dims only
  totalComposite: number;        // blended final score

  // Blend ratio (configurable per mode)
  baseWeight: number;            // e.g., 0.65
  novelWeight: number;           // e.g., 0.35

  // Metadata
  landmarkTier: number;
  detractorPenalty: number;
  detectedFaceCount: number;
  isBurstFrame: boolean;
  burstGroupId: string | null;
}

// ─── Novel Dimension Weights per Mode ───────────────────────

export interface NovelWeightProfile {
  colorHarmony: number;
  depthPerception: number;
  authenticityIndex: number;
  subjectEnvironment: number;
  gazeDynamics: number;
  emotionalResonance: number;
  temporalPeak: number;
  // How much the novel dimensions influence the total score (0-1)
  novelBlendRatio: number;
}

export const NOVEL_MODE_WEIGHTS: Record<string, NovelWeightProfile> = {
  balanced: {
    colorHarmony: 0.18, depthPerception: 0.15, authenticityIndex: 0.15,
    subjectEnvironment: 0.15, gazeDynamics: 0.12, emotionalResonance: 0.15,
    temporalPeak: 0.10, novelBlendRatio: 0.30,
  },
  social: {
    colorHarmony: 0.10, depthPerception: 0.10, authenticityIndex: 0.20,
    subjectEnvironment: 0.10, gazeDynamics: 0.25, emotionalResonance: 0.15,
    temporalPeak: 0.10, novelBlendRatio: 0.35,
  },
  instagram: {
    colorHarmony: 0.25, depthPerception: 0.15, authenticityIndex: 0.05,
    subjectEnvironment: 0.10, gazeDynamics: 0.10, emotionalResonance: 0.25,
    temporalPeak: 0.10, novelBlendRatio: 0.35,
  },
  travel: {
    colorHarmony: 0.20, depthPerception: 0.25, authenticityIndex: 0.10,
    subjectEnvironment: 0.15, gazeDynamics: 0.05, emotionalResonance: 0.20,
    temporalPeak: 0.05, novelBlendRatio: 0.30,
  },
  realEstate: {
    colorHarmony: 0.15, depthPerception: 0.25, authenticityIndex: 0.25,
    subjectEnvironment: 0.10, gazeDynamics: 0.00, emotionalResonance: 0.10,
    temporalPeak: 0.15, novelBlendRatio: 0.25,
  },
  professional: {
    colorHarmony: 0.15, depthPerception: 0.10, authenticityIndex: 0.20,
    subjectEnvironment: 0.15, gazeDynamics: 0.20, emotionalResonance: 0.10,
    temporalPeak: 0.10, novelBlendRatio: 0.30,
  },
  event: {
    colorHarmony: 0.20, depthPerception: 0.10, authenticityIndex: 0.15,
    subjectEnvironment: 0.05, gazeDynamics: 0.05, emotionalResonance: 0.30,
    temporalPeak: 0.15, novelBlendRatio: 0.25,
  },
  sports: {
    colorHarmony: 0.15, depthPerception: 0.30, authenticityIndex: 0.10,
    subjectEnvironment: 0.05, gazeDynamics: 0.05, emotionalResonance: 0.15,
    temporalPeak: 0.20, novelBlendRatio: 0.20,
  },
  custom: {
    colorHarmony: 0.15, depthPerception: 0.15, authenticityIndex: 0.14,
    subjectEnvironment: 0.14, gazeDynamics: 0.14, emotionalResonance: 0.14,
    temporalPeak: 0.14, novelBlendRatio: 0.30,
  },
};

// ─── Admin Batch Configuration ──────────────────────────────

export interface BatchConfig {
  maxPhotos: number;
  concurrentWorkers: number;
  chunkSize: number;              // process in chunks for memory management
  progressReportInterval: number; // report every N photos
  timeoutPerPhotoMs: number;
}

export const BATCH_CONFIGS: Record<'user' | 'admin', BatchConfig> = {
  user: {
    maxPhotos: 500,
    concurrentWorkers: 4,
    chunkSize: 50,
    progressReportInterval: 1,
    timeoutPerPhotoMs: 5000,
  },
  admin: {
    maxPhotos: 100_000,
    concurrentWorkers: 8,
    chunkSize: 500,
    progressReportInterval: 100,
    timeoutPerPhotoMs: 3000,
  },
};

// ─── Edit Plan Summary (embedded in PhotoScore) ─────────────

export interface EditPlanSummary {
  weakestDimension: string;
  strongestDimension: string;
  totalDeficit: number;
  topEdit: { dimension: string; intensity: number; operation: string } | null;
  summary: string;
}

// ─── Social Calibration Engine ─────────────────────────────
// Patent: Novel dimension — Mode-specific social calibration scoring.
// Big tech (Apple, Google) cannot ship these because social calibration
// requires opinionated subjective signals that conflict with platform neutrality.
// FRAME's moat: we score for the CONTEXT the photo will be used in.

// Dating Mode Dimensions (D1–D6)
export type DatingDimensionKey =
  | 'socialProofSignal'      // D1: evidence of social life
  | 'lifestyleBreadth'       // D2: variety of activities/settings
  | 'approachabilityWarmth'  // D3: body language, smile, eye contact warmth
  | 'tastePresentation'      // D4: grooming, style, background curation
  | 'photoSetComposition'    // D5: session-level — how photos work as a SET
  | 'emotionalAvailability'; // D6: openness signals vs guarded body language

// Instagram Mode Dimensions (I1–I5)
export type InstagramDimensionKey =
  | 'scrollStopPower'        // I1: would you stop scrolling?
  | 'aspirationalQuality'    // I2: lifestyle aspiration signal
  | 'aestheticCohesion'      // I3: session-level — feed consistency
  | 'engagementPredictor'    // I4: predicted like/comment ratio
  | 'shareabilityIndex';     // I5: would someone share this?

// Travel Mode Dimensions (T1–T3)
export type TravelDimensionKey =
  | 'iWasThereProof'         // T1: authentic presence marker
  | 'sensoryTranslation'     // T2: can you "feel" the place?
  | 'storyFragment';         // T3: narrative potential

// Event Mode Dimensions (E1–E3)
export type EventDimensionKey =
  | 'groupEnergy'            // E1: collective energy — faces, expressions, scene liveliness
  | 'candidCapture'          // E2: genuine unposed moment quality
  | 'inclusiveFraming';      // E3: everyone visible, balanced framing

// Sports Mode Dimensions (S1–S3)
export type SportsDimensionKey =
  | 'peakAction'             // S1: peak moment / decisive instant capture
  | 'athleteIsolation'       // S2: subject separation from background
  | 'venueAtmosphere';       // S3: venue energy and crowd atmosphere

export type SocialCalibrationKey =
  | DatingDimensionKey
  | InstagramDimensionKey
  | TravelDimensionKey
  | EventDimensionKey
  | SportsDimensionKey;

export interface SocialCalibrationScores {
  // Social (D1–D6) — null when not in social mode
  socialProofSignal: number | null;
  lifestyleBreadth: number | null;
  approachabilityWarmth: number | null;
  tastePresentation: number | null;
  photoSetComposition: number | null;    // session-level
  emotionalAvailability: number | null;

  // Instagram (I1–I5) — null when not in instagram mode
  scrollStopPower: number | null;
  aspirationalQuality: number | null;
  aestheticCohesion: number | null;      // session-level
  engagementPredictor: number | null;
  shareabilityIndex: number | null;

  // Travel (T1–T3) — null when not in travel mode
  iWasThereProof: number | null;
  sensoryTranslation: number | null;
  storyFragment: number | null;

  // Event (E1–E3) — null when not in event mode
  groupEnergy: number | null;
  candidCapture: number | null;
  inclusiveFraming: number | null;

  // Sports (S1–S3) — null when not in sports mode
  peakAction: number | null;
  athleteIsolation: number | null;
  venueAtmosphere: number | null;
}

export interface SocialCalibrationMeta {
  key: SocialCalibrationKey;
  label: string;
  icon: string;
  mode: 'social' | 'instagram' | 'travel' | 'event' | 'sports';
  isSessionLevel: boolean;
  isEditable: boolean;    // can editing improve this?
  description: string;
  editSuggestion?: string;   // for editable dims: what edit helps
  textRecommendation?: string; // for non-editable dims: what to do differently
}

export const SOCIAL_CALIBRATION_DIMENSIONS: SocialCalibrationMeta[] = [
  // Dating D1–D6
  {
    key: 'socialProofSignal', label: 'Social Proof', icon: 'person-2',
    mode: 'social', isSessionLevel: false, isEditable: false,
    description: 'Evidence of social life — friends, events, activities visible in the photo.',
    textRecommendation: 'Include photos with friends at events or activities to signal social proof.',
  },
  {
    key: 'lifestyleBreadth', label: 'Lifestyle Breadth', icon: 'globe',
    mode: 'social', isSessionLevel: false, isEditable: false,
    description: 'Variety of activities and settings — shows a well-rounded life.',
    textRecommendation: 'Mix indoor/outdoor, active/relaxed, social/solo photos for broader appeal.',
  },
  {
    key: 'approachabilityWarmth', label: 'Approachability', icon: 'face-smiling',
    mode: 'social', isSessionLevel: false, isEditable: true,
    description: 'Warmth signals — genuine smile, open body language, eye contact quality.',
    editSuggestion: 'Warm color temperature +200K, brighten eye area, soften shadows on face.',
  },
  {
    key: 'tastePresentation', label: 'Taste & Style', icon: 'paintbrush',
    mode: 'social', isSessionLevel: false, isEditable: true,
    description: 'Grooming, personal style, and background curation quality.',
    editSuggestion: 'Clean up background distractions, boost clarity +15, subtle vignette.',
  },
  {
    key: 'photoSetComposition', label: 'Set Balance', icon: 'rectangle-stack',
    mode: 'social', isSessionLevel: true, isEditable: false,
    description: 'How well photos work together as a set — variety, story arc, no redundancy.',
    textRecommendation: 'Ensure your set includes: 1 clear face shot, 1 full body, 1 social, 1 activity.',
  },
  {
    key: 'emotionalAvailability', label: 'Openness', icon: 'heart',
    mode: 'social', isSessionLevel: false, isEditable: true,
    description: 'Openness signals — relaxed posture, genuine expression, not guarded.',
    editSuggestion: 'Warm the color palette, lift shadows for a more open feel.',
  },

  // Instagram I1–I5
  {
    key: 'scrollStopPower', label: 'Scroll-Stop', icon: 'hand-raised',
    mode: 'instagram', isSessionLevel: false, isEditable: true,
    description: 'Would someone stop scrolling for this? Visual impact in the first 0.3 seconds.',
    editSuggestion: 'Increase contrast +15, boost vibrance +20, sharpen focal point.',
  },
  {
    key: 'aspirationalQuality', label: 'Aspirational', icon: 'star',
    mode: 'instagram', isSessionLevel: false, isEditable: false,
    description: 'Lifestyle aspiration signal — does this photo make someone wish they were there?',
    textRecommendation: 'Choose settings that evoke desire: beautiful locations, great food, experiences.',
  },
  {
    key: 'aestheticCohesion', label: 'Feed Cohesion', icon: 'square-grid-3x3',
    mode: 'instagram', isSessionLevel: true, isEditable: true,
    description: 'How well this photo fits with others in the set — consistent color palette, tone.',
    editSuggestion: 'Apply consistent color grade across set, match warmth and saturation levels.',
  },
  {
    key: 'engagementPredictor', label: 'Engagement', icon: 'bubble-left',
    mode: 'instagram', isSessionLevel: false, isEditable: true,
    description: 'Predicted like/comment ratio based on visual signals that drive engagement.',
    editSuggestion: 'Brighten face, boost saturation +10, ensure strong focal point.',
  },
  {
    key: 'shareabilityIndex', label: 'Shareability', icon: 'arrow-turn-up-right',
    mode: 'instagram', isSessionLevel: false, isEditable: false,
    description: 'Would someone share this? Uniqueness, wow factor, storytelling potential.',
    textRecommendation: 'Capture unique moments, unusual angles, or emotionally resonant scenes.',
  },

  // Travel T1–T3
  {
    key: 'iWasThereProof', label: '"I Was There"', icon: 'mappin',
    mode: 'travel', isSessionLevel: false, isEditable: false,
    description: 'Authentic presence marker — subject in scene, not just a landscape postcard.',
    textRecommendation: 'Include yourself naturally in the scene, not posed tourist-style.',
  },
  {
    key: 'sensoryTranslation', label: 'Sensory Feel', icon: 'wind',
    mode: 'travel', isSessionLevel: false, isEditable: true,
    description: 'Can you "feel" the place? Temperature, texture, atmosphere come through.',
    editSuggestion: 'Enhance atmospheric haze, warm/cool tones to match climate, clarity +10.',
  },
  {
    key: 'storyFragment', label: 'Story Potential', icon: 'book',
    mode: 'travel', isSessionLevel: false, isEditable: false,
    description: 'Narrative potential — does this photo tell part of a story?',
    textRecommendation: 'Capture moments of action, interaction, or discovery rather than static poses.',
  },

  // Event E1–E3
  {
    key: 'groupEnergy', label: 'Group Energy', icon: 'person-3',
    mode: 'event', isSessionLevel: false, isEditable: false,
    description: 'Collective energy — genuine expressions, group cohesion, scene liveliness.',
    textRecommendation: 'Capture moments when the group is genuinely engaged — laughter, toasts, dancing.',
  },
  {
    key: 'candidCapture', label: 'Candid Quality', icon: 'camera',
    mode: 'event', isSessionLevel: false, isEditable: false,
    description: 'Genuine unposed moment — natural expressions and body language.',
    textRecommendation: 'Shoot continuously during key moments rather than asking people to pose.',
  },
  {
    key: 'inclusiveFraming', label: 'Inclusive Framing', icon: 'rectangle-group',
    mode: 'event', isSessionLevel: false, isEditable: true,
    description: 'Everyone visible and balanced in frame — no one cut off or hidden.',
    editSuggestion: 'Crop to include all faces evenly, straighten horizon, balance visual weight.',
  },

  // Sports S1–S3
  {
    key: 'peakAction', label: 'Peak Action', icon: 'bolt',
    mode: 'sports', isSessionLevel: false, isEditable: false,
    description: 'Decisive instant — peak of motion, celebration, or athletic achievement.',
    textRecommendation: 'Use burst mode and anticipate the peak moment of action.',
  },
  {
    key: 'athleteIsolation', label: 'Subject Isolation', icon: 'person',
    mode: 'sports', isSessionLevel: false, isEditable: true,
    description: 'Subject cleanly separated from background — focus draws to the athlete.',
    editSuggestion: 'Increase background blur, darken distractions, boost subject clarity.',
  },
  {
    key: 'venueAtmosphere', label: 'Venue Atmosphere', icon: 'building',
    mode: 'sports', isSessionLevel: false, isEditable: true,
    description: 'Energy of the venue — crowd, lighting, scale convey the atmosphere.',
    editSuggestion: 'Boost vibrancy, enhance crowd lighting, increase clarity in venue details.',
  },
];

// Social calibration weight profiles per mode
export interface SocialCalibrationWeightProfile {
  weights: Partial<Record<SocialCalibrationKey, number>>;
  blendRatio: number; // how much social calibration influences final score (0-1)
}

export const SOCIAL_CALIBRATION_WEIGHTS: Record<string, SocialCalibrationWeightProfile> = {
  social: {
    weights: {
      socialProofSignal: 0.12,
      lifestyleBreadth: 0.10,
      approachabilityWarmth: 0.25,
      tastePresentation: 0.18,
      photoSetComposition: 0.20,
      emotionalAvailability: 0.15,
    },
    blendRatio: 0.40, // Social calibration is 40% of social mode score
  },
  instagram: {
    weights: {
      scrollStopPower: 0.30,
      aspirationalQuality: 0.20,
      aestheticCohesion: 0.15,
      engagementPredictor: 0.20,
      shareabilityIndex: 0.15,
    },
    blendRatio: 0.35,
  },
  travel: {
    weights: {
      iWasThereProof: 0.35,
      sensoryTranslation: 0.35,
      storyFragment: 0.30,
    },
    blendRatio: 0.30,
  },
  event: {
    weights: {
      groupEnergy: 0.40,
      candidCapture: 0.35,
      inclusiveFraming: 0.25,
    },
    blendRatio: 0.30,
  },
  sports: {
    weights: {
      peakAction: 0.40,
      athleteIsolation: 0.30,
      venueAtmosphere: 0.30,
    },
    blendRatio: 0.25,
  },
};
